import React, { useMemo, useState } from "react";
import { Award, ArrowRight, MapPinned } from "lucide-react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";

const REGION_CONFIG = [
  { name: "Jakarta Barat", color: "#0B6B3A" },
  { name: "Jakarta Pusat", color: "#27AE60" },
  { name: "Jakarta Selatan", color: "#63C987" },
  { name: "Jakarta Timur", color: "#9DDEB4" },
  { name: "Jakarta Utara", color: "#F2C94C" },
  { name: "Kepulauan Seribu", color: "#D6B03F" },
];

const SUBDISTRICT_COLORS = [
  "#0B6B3A",
  "#27AE60",
  "#63C987",
  "#9DDEB4",
  "#F2C94C",
  "#D6B03F",
];

function getRegionCount(distribution, regionName) {
  const match = distribution.find((item) => (
    String(item.region || "").trim().toLocaleLowerCase("id-ID")
      === regionName.toLocaleLowerCase("id-ID")
  ));
  return Number(match?.count || 0);
}

function DonutTooltip({ active, payload }) {
  if (!active || !payload?.length) {
    return null;
  }

  const item = payload[0].payload;
  return (
    <div
      className="min-w-[160px] rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-lg"
      data-testid="recipient-donut-tooltip"
    >
      <p className="whitespace-nowrap text-xs font-bold text-[#1F2937]">{item.name}</p>
      <p className="mt-0.5 whitespace-nowrap text-xs text-[#0B6B3A]">
        {item.count} penerima manfaat
      </p>
    </div>
  );
}

export default function RegionalDonutChart({
  distribution,
  totalRecipients,
  onViewParticipants,
  isRegionalAdmin = false,
  regionName = "",
}) {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const chartData = useMemo(() => {
    if (isRegionalAdmin) {
      const subdistrictData = distribution.map((item, index) => ({
        name: item.subdistrict || "Belum diisi",
        count: Number(item.count || 0),
        color: SUBDISTRICT_COLORS[index % SUBDISTRICT_COLORS.length],
        isSelectable: true,
      }));

      if (!subdistrictData.length) {
        return [{
          name: "Belum ada data kecamatan",
          count: 0,
          value: 1,
          color: "#D1D5DB",
          isSelectable: false,
        }];
      }

      return subdistrictData.map((item) => ({
        ...item,
        value: totalRecipients > 0 ? item.count : 1,
      }));
    }

    return REGION_CONFIG.map((region) => {
      const count = getRegionCount(distribution, region.name);
      return {
        ...region,
        count,
        value: totalRecipients > 0 ? count : 1,
        isSelectable: true,
      };
    });
  }, [distribution, isRegionalAdmin, totalRecipients]);
  const selectedRegion = selectedIndex === null ? null : chartData[selectedIndex];

  const selectRegion = (index) => {
    if (chartData[index]?.isSelectable) {
      setSelectedIndex(index);
    }
  };

  const geographyLabel = isRegionalAdmin ? "Kecamatan" : "Wilayah";
  const dashboardTitle = isRegionalAdmin
    ? `Penerima Manfaat ${regionName || "Wilayah Kerja"}`
    : "Penerima Manfaat DKI Jakarta";

  return (
    <section
      className="grid grid-cols-1 gap-6 lg:grid-cols-12"
      data-testid="recipient-donut-dashboard"
    >
      <div
        className="overflow-hidden rounded-lg border border-gray-200 bg-white lg:col-span-8"
        data-testid="recipient-donut-card"
      >
        <div className="border-b border-gray-100 px-6 py-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0B6B3A]">
            Sebaran {geographyLabel}
          </p>
          <h3 className="mt-2 font-display text-xl font-extrabold text-[#1F2937]">
            Grafik {dashboardTitle}
          </h3>
          <p className="mt-1 text-sm text-[#6B7280]">
            Pilih segmen atau nama {geographyLabel.toLowerCase()} untuk melihat rincian penerima.
          </p>
        </div>

        <div className="grid gap-6 px-5 py-6 sm:px-6 md:grid-cols-[minmax(250px,0.9fr)_1.1fr]">
          <div
            className="relative mx-auto aspect-square w-full max-w-[330px]"
            data-testid="recipient-donut-chart"
          >
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Tooltip
                  content={<DonutTooltip />}
                  offset={18}
                  allowEscapeViewBox={{ x: true, y: true }}
                  wrapperStyle={{ zIndex: 30, pointerEvents: "none" }}
                />
                <Pie
                  data={chartData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius="58%"
                  outerRadius="88%"
                  paddingAngle={3}
                  stroke="none"
                  isAnimationActive={false}
                  onClick={(_, index) => selectRegion(index)}
                >
                  {chartData.map((region, index) => (
                    <Cell
                      key={region.name}
                      fill={region.color}
                      opacity={selectedIndex === null || selectedIndex === index ? 1 : 0.42}
                      className={region.isSelectable ? "cursor-pointer outline-none" : "outline-none"}
                      data-testid={`recipient-donut-segment-${index}`}
                    />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>

            <div
              className="pointer-events-none absolute inset-0 m-auto flex h-[42%] w-[42%] flex-col"
              data-testid="recipient-donut-center"
            >
              <div className="flex h-full w-full flex-col items-center justify-center rounded-full border-8 border-white bg-[#F9FAFB] px-3 text-center shadow-sm">
                <Award className="h-5 w-5 text-[#27AE60]" aria-hidden="true" />
                <p className="mt-1 font-display text-3xl font-extrabold text-[#0B6B3A]">
                  {totalRecipients}
                </p>
                <p className="mt-0.5 text-[10px] font-bold uppercase tracking-wide text-[#6B7280]">
                  Penerima
                </p>
              </div>
            </div>
          </div>

          <div className="grid content-center gap-2" data-testid="recipient-donut-legend">
            {chartData.map((region, index) => {
              const percentage = totalRecipients > 0
                ? Math.round((region.count / totalRecipients) * 100)
                : 0;
              const isSelected = selectedIndex === index;

              return (
                <button
                  key={region.name}
                  type="button"
                  onClick={() => selectRegion(index)}
                  disabled={!region.isSelectable}
                  data-testid={`recipient-donut-legend-${index}`}
                  className={[
                    "flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 text-left",
                    "transition-colors",
                    !region.isSelectable
                      ? "cursor-default border-transparent bg-[#F9FAFB]"
                      : isSelected
                      ? "border-[#27AE60] bg-[#F0FBF5]"
                      : "border-transparent bg-[#F9FAFB] hover:border-[#B7E4C7] hover:bg-[#F0FBF5]",
                  ].join(" ")}
                >
                  <span className="flex min-w-0 items-center gap-2.5">
                    <span
                      className="h-3 w-3 shrink-0 rounded-full"
                      style={{ backgroundColor: region.color }}
                      aria-hidden="true"
                    />
                    <span className="truncate text-sm font-bold text-[#1F2937]">{region.name}</span>
                  </span>
                  <span className="shrink-0 text-sm font-extrabold text-[#0B6B3A]">
                    {percentage}%
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <aside
        className="flex min-h-[296px] flex-col rounded-lg border border-gray-200 bg-white p-6 lg:col-span-4"
        data-testid="recipient-donut-detail-panel"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0B6B3A]">
              {selectedRegion
                ? `${geographyLabel} Terpilih`
                : isRegionalAdmin
                  ? "Ringkasan Wilayah Kerja"
                  : "Ringkasan DKI Jakarta"}
            </p>
            <h3 className="mt-2 font-display text-2xl font-extrabold text-[#1F2937]">
              {selectedRegion?.name || (isRegionalAdmin ? regionName || "Wilayah Kerja" : "Seluruh Wilayah")}
            </h3>
          </div>
          <MapPinned className="h-6 w-6 shrink-0 text-[#27AE60]" aria-hidden="true" />
        </div>

        <div className="mt-8 border-l-4 border-[#27AE60] pl-4">
          <p className="text-sm font-semibold text-[#6B7280]">Penerima Manfaat</p>
          <p className="mt-1 font-display text-5xl font-extrabold text-[#0B6B3A]">
            {selectedRegion?.count ?? totalRecipients}
          </p>
        </div>

        <p className="mt-6 text-sm leading-6 text-[#6B7280]" data-testid="recipient-donut-detail-copy">
          {selectedRegion
            ? isRegionalAdmin
              ? `Buka Data Peserta dalam cakupan ${regionName || "wilayah kerja"} Anda.`
              : `Tampilkan daftar penerima manfaat di ${selectedRegion.name}.`
            : `Pilih satu segmen grafik untuk melihat rincian ${geographyLabel.toLowerCase()}.`}
        </p>

        <button
          type="button"
          onClick={() => selectedRegion && onViewParticipants(
            isRegionalAdmin ? undefined : selectedRegion.name,
          )}
          disabled={!selectedRegion}
          data-testid="recipient-donut-detail-panel-cta"
          className={[
            "mt-auto inline-flex w-full items-center justify-center gap-2 rounded-lg px-4 py-3",
            "text-sm font-bold transition-colors",
            selectedRegion
              ? "bg-[#0B6B3A] text-white hover:bg-[#07532d]"
              : "cursor-not-allowed bg-gray-100 text-gray-400",
          ].join(" ")}
        >
          {isRegionalAdmin ? "Lihat Data Peserta Wilayah" : "Lihat Data Peserta"}
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </button>
      </aside>
    </section>
  );
}